import { useState, useMemo, useCallback } from 'react';

import { internalRequest } from '@/utils/internalRequest';

import { useUrl } from '../useUrl';
import { useValidation } from '../useValidation';

import type { UseUrlProps, UseUrl } from '../useUrl';
import type { ErrorFields, Fields } from '../useValidation';
import type { CreateResponse, UpdateResponse, DeleteResponse } from '@/utils/internalRequest';
import type { SetStateAction, Dispatch } from 'react';

export interface ResourceProps<ResponseBody, RequestBody> extends Omit<UseUrlProps<ResponseBody>, 'url'> {
  endpoint: string;
  fields: Fields;
  initialFormState?: Partial<RequestBody>;
  id?: string | number;
  onSaved?: (response: UpdateResponse<ResponseBody> | CreateResponse<ResponseBody>) => void;
  onDestroyed?: () => void;
}

export interface UseResource<ResponseBody, RequestBody> extends UseUrl<ResponseBody> {
  url: string;
  isNew: boolean;
  canUpdate: boolean;
  canDestroy: boolean;
  isValid: boolean;
  isSaving: boolean;
  formHasNoErrors: boolean;
  formErrors: ErrorFields | null;
  formData: Partial<RequestBody>;
  handleValue: (key: string, value: unknown) => void;
  setFormData: Dispatch<SetStateAction<Partial<RequestBody>>>;
  handleDestroy(): Promise<boolean>;
  handleSave(
    extraData?: Partial<RequestBody>,
  ): Promise<UpdateResponse<ResponseBody> | CreateResponse<ResponseBody> | null>;
  clearFormData: () => void;
}

export function useResource<ResponseBody = unknown, RequestBody = ResponseBody>({
  endpoint,
  id,
  pause,
  fields,
  initial,
  initialFormState,
  onDestroyed,
  onSaved,
}: ResourceProps<ResponseBody, RequestBody>): UseResource<ResponseBody, RequestBody> {
  const [isSaving, setIsSaving] = useState(false);
  const [formState, setFormData] = useState<Partial<RequestBody>>(initialFormState || {});
  const [saveError, setSaveError] = useState<string | null>(null);

  const url = `${endpoint}${id ? `/${id}` : ''}`;
  const isNew = !id;
  const canDestroy = !isNew;
  const canUpdate = !isNew;

  const {
    data,
    isLoading,
    error: dataError,
    refetch,
  } = useUrl<ResponseBody>({
    url,
    pause: pause || isNew, // do not try to fetch resource when it is new
    initial,
  });

  // merge original data and form state into one
  const formData = useMemo(
    () => ({
      ...data,
      ...formState,
    }),
    [data, formState],
  );

  const { isValid, errors: formErrors } = useValidation({
    data: formData,
    configurationFields: fields,
  });

  const formHasNoErrors = useMemo(() => formErrors && Object.keys(formErrors).length === 0, [formErrors]);

  // save function stores data that is set in state
  // extra data can be appended when saving without the need to put it in state first (ex. { status: 'draft'})
  const handleSave = useCallback(
    async (extraData: Partial<RequestBody>) => {
      setIsSaving(true);

      const body = {
        ...formData, // the updated data
        ...extraData, // extra data you can pass when using save function
      };
      try {
        const response = await internalRequest<ResponseBody>({
          url,
          method: isNew ? 'POST' : 'PUT',
          body,
          headers: {
            'Content-Type': 'application/json',
          },
        });
        setIsSaving(false);
        if (onSaved && response) onSaved(response);
        setFormData({});
        return response;
      } catch (err: unknown) {
        setIsSaving(false);
        setSaveError((err as Error).message);

        return null;
      }
    },
    [formData, url, isNew, onSaved],
  );

  const handleDestroy = useCallback(async () => {
    if (!canDestroy) {
      return false;
    }
    try {
      await internalRequest<DeleteResponse>({ url, method: 'DELETE' });
      if (onDestroyed) onDestroyed();
      return true;
    } catch (err: unknown) {
      return false;
    }
  }, [canDestroy, onDestroyed, url]);

  const handleValue = useCallback((key: string, value: unknown) => {
    setFormData((prevData) => ({
      ...(prevData || {}),
      [key]: value,
    }));
  }, []);

  const clearFormData = useCallback(() => {
    setFormData({});
  }, []);

  return {
    url,
    isNew,
    canDestroy,
    canUpdate,
    data,
    formData,
    isValid,
    formErrors,
    error: dataError || saveError || undefined,
    isLoading,
    isSaving,
    formHasNoErrors,
    setFormData,
    handleValue,
    refetch,
    handleSave,
    handleDestroy,
    clearFormData,
  };
}
